/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import moment from 'moment/moment';
import { useHistory } from 'react-router-dom';
import {
  LoanPromoTitle,
  Body,
  Filter,
  LeftPanel,
  List,
  Tabs,
  MAIN_ICON_COLORS,
} from 'assets/components';
import PromoImage from 'assets/images/loans-promo.png';
import { LOAN_STATUS, MODAL_SCREENS } from './constants';
import { alphaSort, filterUsableWallets } from '../../utilities/helpers';
import {
  useLedgerFilteredCurrencies,
  useLedgerFilteredWallets,
} from '../../utilities/sharedHooks/ledgerWallets';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
  USER_REGION_SETTINGS_QUERY_KEY,
  USER_DETAILS_QUERY_KEY,
} from '../../utilities/sharedQueries';
import UnlockLiquidity from './partials/unlockLiquidity';
import { WALLETS_ALL } from '../Accounts/queries';
import LoansLayout from '../shared/LoansLayout';
import ModalLayout from '../shared/ModalLayout';
import RepayLoan from './modals/repayLoan';
import { useDefaultLoanCollateralWallet } from './hooks';
import LoanCalculator from './modals/loanCalculator';
import LoanDetails from './modals/loanDetails';
import LoanOffer from './modals/loanOffer';
import LoanTerms from './modals/loanTerms';
import PayInterest from './modals/payInterest';
import Wallets from './modals/wallets';
import { LoanItem } from './partials/loanItem';
import { LOANS_ALL } from './queries';
import LoanMoreCollateral from './modals/loanMoreCollateral';
import { useSetting } from '../../utilities/sharedHooks/settings';
import { currencies, exchange } from '../../utilities/constants';
import WalletDepositModal from '../Accounts/modals/WalletsWithdrawDepositModal';
import { walletCurrenciesTypes } from '../Accounts/constants';
import ExchangeModal from '../Accounts/modals/exchangeModal';
import AddMoreCollateral from '../Card/addMoreCollateral';
import PGWProceedWithPayment from '../Card/pgwProceedPayment';
import InfoPopup from '../shared/Modals/InfoPopup';
import { formatNumber } from '../../utilities/formatHelpers';
import useIsUsUser from '../../utilities/sharedHooks/usUser';
import { mainStakingCurrency } from '../Staking/constants';
import { useLedgerAccounts } from '../../utilities/sharedHooks/ledgerSDKHooks';
import { FLOWS, sendStats } from '../../services/mixpanel';
import { MXP_FTC_GET_VISIT } from './mixpanel-events';
import { SkeletonPageLoader } from '../../components';

const LOANS_FILTERS = [
  { label: 'Currency', col: 4, isLabel: true },
  {
    label: 'Available balance',
    col: 3,
    withIcon: true,
    isNumber: true,
    sorting_key: 'available_balance',
  },
  { label: 'Loan due date', col: 3, withIcon: true, sorting_key: 'loan_due_at_ts', isNumber: true },
  { label: 'Actions', col: 2, isRight: true, isLabel: true },
];

const SETTLED_LOANS_FILTERS = LOANS_FILTERS.map((loan) => ({ ...loan }));
SETTLED_LOANS_FILTERS[SETTLED_LOANS_FILTERS.length - 1].isHidden = true;

const activeAndPendingLoansFilter = (loan) =>
  loan.status === LOAN_STATUS.ACTIVE || loan.status === LOAN_STATUS.PENDING;
const settledLoansFilter = (loan) => loan.status === LOAN_STATUS.SETTLED;

const Loans = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('');
  const [selectedLoan, setSelectedLoan] = useState();
  const [allLoans, setAllLoans] = useState([]);
  const { data: userData, isLoading: isLoadingUserData } = useQuery(USER_DETAILS_QUERY_KEY);
  const isUSUser = useIsUsUser();
  const [previousScreen, setPreviousScreen] = useState();
  const [currentWallet, setCurrentWallet] = useState();
  const { data: userPref } = useQuery(USER_PREFERENCES);
  const fiatCurrencyCode = userPref?.displayCurrency || 'EUR';
  const [ledgerAccounts] = useLedgerAccounts();
  const [showPromoLoans, setShowPromoLoans] = useState(false);

  const [loansEnabled, isLoadingSettings] = useSetting('app_features', 'feature_allow_loans');

  const {
    refetch: fetchAllLoans,
    isLoading: isLoadingLoans,
    isFetching,
  } = useQuery(LOANS_ALL, {
    onSettled(data, error) {
      if (!error) {
        const _loansWithSortingKeys = data?.map((loan) => {
          const _balance = formatNumber(
            loan.net_principal_amount *
              currencies[loan.principal_currency.toUpperCase()].conversion,
            exchange.FIAT_VALUE
          );
          loan.available_balance = parseFloat(_balance);
          loan.loan_due_at_ts = moment(loan.loan_due_at).valueOf();

          return loan;
        });

        setAllLoans(_loansWithSortingKeys);
        setShowPromoLoans(data.filter(activeAndPendingLoansFilter).length === 0);
      }
    },
  });

  useEffect(() => {
    fetchAllLoans();
  }, []);

  // Redirect to card page if loans are disabled
  useEffect(() => {
    if (loansEnabled !== null && loansEnabled === false && !isLoadingSettings) {
      history.push('/');
    }
  }, [loansEnabled, isLoadingSettings]);

  const [sortingOptions, setSortingOptions] = useState({
    ascending: false,
    columnKey: 'coin',
    castToNumber: true,
  });

  const handleSortLoans = (columnKey, castToNumber = true) => {
    setSortingOptions((prev) => ({
      columnKey,
      castToNumber,
      ascending: !prev.ascending,
    }));
  };
  const [sortedLoans, setSortedLoans] = useState([]);

  useEffect(() => {
    if (allLoans) {
      const comparerFn = (a, b) =>
        alphaSort(
          a,
          b,
          sortingOptions.columnKey,
          sortingOptions.ascending,
          sortingOptions.castToNumber
        );

      const _allLoans = allLoans?.sort(comparerFn);
      setSortedLoans(_allLoans || []);
    } else {
      setSortedLoans([]);
    }
  }, [allLoans, sortingOptions]);

  const { data: wallets } = useLedgerFilteredWallets(WALLETS_ALL);
  const useableWallets = useMemo(() => filterUsableWallets(wallets || []), [wallets]);
  const { data: ledgerFilteredCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const principalAllowedCurrencies = useMemo(() => {
    if (ledgerFilteredCurrencies) {
      return Object.values(ledgerFilteredCurrencies).filter((c) => {
        let allowedWallets = c.allow_as_loan_payout;
        if (isUSUser) {
          allowedWallets = c.currency_code === 'usd' && c.allow_as_loan_payout;
        }
        return allowedWallets;
      });
    }

    return [];
  }, [useableWallets, ledgerFilteredCurrencies, isUSUser]);

  const [calculatorForm, updateCalculatorForm] = useState({
    principalWallet: null,
    collateralWallet: null,
    apr: null,
    ltv: null,
    principalAmount: null,
    principalAmountDecimal: null,
    collateralAmount: null,
    loanLength: null,
    totalInterest: null,
    isPayoutWallet: false,
    user: userData?.user,
    achAccount: null,
    maxwellAccountDDA: null,
    monthlyIncome: 1000,
    purposeOfLoan: null,
  });

  const resetCalculatorForm = () => {
    updateCalculatorForm((prev) => ({
      ...prev,
      apr: null,
      ltv: null,
      principalAmount: null,
      principalAmountDecimal: null,
      collateralAmount: null,
      loanLength: null,
      totalInterest: null,
      isPayoutWallet: false,
      user: userData?.user,
      achAccount: null,
      maxwellAccountDDA: null,
      monthlyIncome: 1000,
      purposeOfLoan: null,
    }));
  };

  const collateralAllowedWallets = useMemo(() => {
    if (useableWallets && ledgerFilteredCurrencies) {
      return useableWallets?.filter(
        (w) =>
          ledgerFilteredCurrencies[w.coin]?.allow_as_loan_collateral ||
          (calculatorForm?.allowBXXAsCollateral && w.coin === mainStakingCurrency)
      );
    }
    return [];
  }, [useableWallets, ledgerFilteredCurrencies, calculatorForm]);

  const selectedCollateralWallet = useDefaultLoanCollateralWallet(collateralAllowedWallets);

  const [pgwRequest, updatePgwRequest] = useState({
    params: {},
  });

  const [extraCollateralOptions, setExtraCollateralOptions] = useState({
    collateralWallet: null,
    minCollateralAmountNeeded: 0.0,
  });

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showReceiveCryptoModal, setShowReceiveCryptoModal] = useState(false);
  const [showExchangeModal, setShowExchangeModal] = useState(false);

  useEffect(() => {
    if (
      principalAllowedCurrencies &&
      principalAllowedCurrencies.length > 0 &&
      !calculatorForm.principalWallet
    ) {
      updateCalculatorForm({
        ...calculatorForm,
        principalWallet: principalAllowedCurrencies[0],
      });
    }
  }, [principalAllowedCurrencies, calculatorForm]);

  useEffect(() => {
    if (selectedCollateralWallet && !calculatorForm.collateralWallet) {
      updateCalculatorForm({
        ...calculatorForm,
        collateralWallet: selectedCollateralWallet,
      });
    }
  }, [selectedCollateralWallet]);

  const showScreen = (screen, loan) => {
    resetCalculatorForm();
    setPreviousScreen(currentScreen);
    setCurrentScreen(screen);
    setIsModalVisible(true);
    if (loan) {
      setSelectedLoan(loan);
    }
  };

  const [newLoanEnabled] = useSetting('app_features', 'feature_allow_new_loans');

  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);

  const isAddLoanDisabled = useMemo(
    () =>
      !userRegionSettings?.can_request_loan ||
      !principalAllowedCurrencies ||
      principalAllowedCurrencies.length === 0 ||
      !calculatorForm.collateralWallet ||
      !calculatorForm.principalWallet ||
      !newLoanEnabled,
    [
      userRegionSettings,
      principalAllowedCurrencies,
      calculatorForm.principalWallet,
      calculatorForm.collateralWallet,
      newLoanEnabled,
    ]
  );

  const promoScreenLabels = useMemo(() => {
    if (isUSUser) {
      return [
        'promo_us_loans_sublabel_1',
        'promo_us_loans_sublabel_2',
        'promo_us_loans_sublabel_3',
        'promo_us_loans_sublabel_4',
      ];
    }

    return [
      'promo_loans_sublabel_1',
      'promo_loans_sublabel_2',
      'promo_loans_sublabel_3',
      'promo_loans_sublabel_4',
    ];
  }, [isUSUser]);

  const renderButtonSection = () => (
    <button
      type="button"
      onClick={() => {
        sendStats(MXP_FTC_GET_VISIT, {}, FLOWS.FTC);
        showScreen(MODAL_SCREENS.ADD_LOAN);
      }}
      disabled={isAddLoanDisabled}
      style={{ maxWidth: '23.125rem' }}
      className="btn btn-primary mt-5 w-100">
      {!userRegionSettings?.can_request_loan
        ? translate('promo_loans_coming_soon_label')
        : translate('promo_loans_start_button_label')}
    </button>
  );

  const renderInfoPopup = () => (
    <div
      style={{ top: '26%', right: '5%', padding: '1rem 1.25rem' }}
      className="position-absolute shadow bg-disabled rounded border-1 popup-info custom-popup-info w-50">
      <i
        className="position-absolute shadow"
        style={{ top: '-12%', right: '5.5%', transform: 'rotate(90deg)' }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="15"
          viewBox="0 0 8 15"
          fill="none">
          <path d="M0 7.88574L8 14.8857L8 0.885742L0 7.88574Z" fill={MAIN_ICON_COLORS.INFO_ARROW} />
        </svg>
      </i>
      <p className="text-white text-f6 fw-bold">{translate('loan_calculator_info_modal_title')}</p>
      <p className="text-white text-f6 mt-2">{translate('loan_calculator_info_modal_content')}</p>
    </div>
  );

  const infoPopupOptions = {
    title: translate('loan_calculator_info_modal_title'),
    content: () => (
      <p className="text-white text-f6 mt-2">{translate('loan_calculator_info_modal_content')}</p>
    ),
    mainScreen: MODAL_SCREENS.ADD_LOAN,
    withBack: true,
    isButtonInFooter: false,
    isScrollable: false,
  };

  const payoutInfoPopupOptions = {
    title: translate('choose_payout_currency_info_modal_title'),
    content: () => (
      <p className="text-white text-f6 mt-2">
        {translate('choose_payout_currency_info_modal_content')}
      </p>
    ),
    mainScreen: MODAL_SCREENS.ADD_LOAN,
    withBack: true,
    isButtonInFooter: false,
    isScrollable: false,
  };

  if (isLoadingLoans || isFetching || isLoadingSettings || isLoadingUserData) {
    return <SkeletonPageLoader />;
  }

  return (
    <LoansLayout hasHeader={false}>
      {showPromoLoans && (
        <div
          className="loans-promo-container pb-5 px-2 pt-0 p-md-5"
          style={{ borderRadius: '1.5rem' }}>
          <div className="row flex-row justify-content-center align-items-center">
            <div className="col-12 col-md-6 px-5 px-md-0">
              <img
                className={`w-100 ${!process.env.REACT_APP_FLAVOR ? 'px-md-5' : ''}`}
                alt="no-loans"
                src={PromoImage}
                loading="eager"
              />
            </div>
            <div className="col-12 d-block d-md-none mt-4">
              <LoanPromoTitle />
            </div>
            <div className="col-12 col-md-6">
              <div className="col-md-10 m-auto">
                <div className="d-none d-md-block">
                  <LoanPromoTitle />
                </div>
                <ul className="mt-4 ps-3 position-relative" style={{ left: '0.3rem' }}>
                  <li className="mt-2">{translate(promoScreenLabels[0])}</li>
                  <li className="mt-3">{translate(promoScreenLabels[1])}</li>
                  <li className="mt-3">{translate(promoScreenLabels[2])}</li>
                  <li className="mt-3">{translate(promoScreenLabels[3])}</li>
                </ul>
                <div className="d-none d-md-block">{renderButtonSection()}</div>
              </div>
            </div>
          </div>
          <div className="d-block d-md-none d-flex flex-column justify-content-center align-items-center">
            {renderButtonSection()}{' '}
          </div>
        </div>
      )}

      {!showPromoLoans && (
        <>
          <h1 className="d-block d-md-none">{translate('loans_tab_label')}</h1>
          <LeftPanel>
            <UnlockLiquidity
              icon="icon-credit-limit-other"
              disabled={isAddLoanDisabled}
              onClick={() => {
                sendStats(MXP_FTC_GET_VISIT, {}, FLOWS.FTC);
                showScreen(MODAL_SCREENS.ADD_LOAN);
              }}
              label={translate('loans_add_loan_label')}
              subLabel={translate('loans_add_loan_sublabel')}
            />
            <></>
          </LeftPanel>
          <Body>
            <Tabs
              keys={[
                { keyName: 'Active', label: translate('loans_loan_active_label') },
                {
                  keyName: 'Settled',
                  label: translate('loans_loan_settled_label'),
                },
              ]}
              renderInfoPopup={renderInfoPopup}>
              <Filter keyName="Active" filters={LOANS_FILTERS} handleSortLoans={handleSortLoans}>
                <List
                  isLoading={isLoadingLoans || isFetching || sortedLoans.length === 0}
                  data={sortedLoans}
                  itemList={
                    <LoanItem onClick={(loan) => showScreen(MODAL_SCREENS.LOAN_DETAILS, loan)} />
                  }
                  filter={activeAndPendingLoansFilter}
                />
              </Filter>
              <Filter
                keyName="Settled"
                filters={SETTLED_LOANS_FILTERS}
                handleSortLoans={handleSortLoans}>
                <List
                  isLoading={isLoadingLoans || isFetching || sortedLoans.length === 0}
                  data={sortedLoans}
                  itemList={
                    <LoanItem onClick={(loan) => showScreen(MODAL_SCREENS.LOAN_DETAILS, loan)} />
                  }
                  filter={settledLoansFilter}
                />
              </Filter>
            </Tabs>
          </Body>
        </>
      )}

      <ModalLayout
        onHide={() => setIsModalVisible(false)}
        isVisible={isModalVisible}
        currentScreen={currentScreen}>
        <LoanDetails
          allLoans={allLoans}
          loanMetaData={selectedLoan}
          keyName={MODAL_SCREENS.LOAN_DETAILS}
          showScreen={showScreen}
        />
        <InfoPopup
          keyName={MODAL_SCREENS.DETAILS_INFO}
          options={infoPopupOptions}
          openScreen={setCurrentScreen}
        />
        <InfoPopup
          keyName={MODAL_SCREENS.PAYOUT_DETAILS_INFO}
          options={payoutInfoPopupOptions}
          openScreen={setCurrentScreen}
        />
        <LoanCalculator
          keyName={MODAL_SCREENS.ADD_LOAN}
          screensKeys={MODAL_SCREENS}
          calculatorForm={calculatorForm}
          updateCalculatorForm={updateCalculatorForm}
          openScreen={setCurrentScreen}
          displayAddCollateral={() => setShowReceiveCryptoModal(true)}
          setExtraCollateralOptions={setExtraCollateralOptions}
          wallets={useableWallets}
        />
        <Wallets
          keyName={MODAL_SCREENS.WALLETS}
          openScreen={setCurrentScreen}
          mainScreen={
            previousScreen === MODAL_SCREENS.PAY_INTEREST
              ? MODAL_SCREENS.PAY_INTEREST
              : MODAL_SCREENS.ADD_LOAN
          }
          wallets={
            calculatorForm.isPayoutWallet ? principalAllowedCurrencies : collateralAllowedWallets
          }
          setCurrentWallet={setCurrentWallet}
          updateCalculatorForm={updateCalculatorForm}
          calculatorForm={calculatorForm}
          isPayoutWallet={calculatorForm.isPayoutWallet}
          useableWallets={useableWallets}
          isPayInterest={previousScreen === MODAL_SCREENS.PAY_INTEREST}
        />
        <LoanOffer
          keyName={MODAL_SCREENS.LOAN_OFFER}
          calculatorForm={calculatorForm}
          screensKeys={MODAL_SCREENS}
          openScreen={setCurrentScreen}
        />
        <LoanTerms
          keyName={MODAL_SCREENS.LOAN_TERMS}
          calculatorForm={calculatorForm}
          screensKeys={MODAL_SCREENS}
          openScreen={setCurrentScreen}
          onHide={() => setIsModalVisible(false)}
        />
        <RepayLoan
          loanDataObj={selectedLoan}
          keyName={MODAL_SCREENS.REPAY_LOAN}
          showScreen={() => {
            showScreen(MODAL_SCREENS.LOAN_DETAILS, selectedLoan.loanMetaData);
          }}
          hideModal={() => {
            fetchAllLoans();
            setIsModalVisible(false);
          }}
        />
        <LoanMoreCollateral
          keyName={MODAL_SCREENS.LOAN_MORE_COLLATERAL}
          openScreen={setCurrentScreen}
          loanMetaData={selectedLoan}
          onHide={() => {
            fetchAllLoans();
            setIsModalVisible(false);
          }}
          setExtraCollateralOptions={setExtraCollateralOptions}
          onExchangeClick={() => setShowExchangeModal(true)}
          onReceiveClick={() => setShowReceiveCryptoModal(true)}
        />
        <PayInterest
          loanDataObj={selectedLoan}
          keyName={MODAL_SCREENS.PAY_INTEREST}
          showScreen={showScreen}
          hideModal={() => {
            fetchAllLoans();
            setIsModalVisible(false);
          }}
          currentWallet={currentWallet}
          setExtraCollateralOptions={setExtraCollateralOptions}
          onExchangeClick={() => setShowExchangeModal(true)}
          onReceiveClick={() => setShowReceiveCryptoModal(true)}
          setCurrentWallet={setCurrentWallet}
          useableWallets={useableWallets}
        />
        <AddMoreCollateral
          keyName={MODAL_SCREENS.ADD_MORE_COLLATERAL}
          wallet={calculatorForm?.collateralWallet || {}}
          mainScreen={MODAL_SCREENS.ADD_LOAN}
          openScreen={setCurrentScreen}
          updatePgwRequest={updatePgwRequest}
        />
        <PGWProceedWithPayment
          keyName={MODAL_SCREENS.PROCEED_WITH_PAYMENT}
          openScreen={setCurrentScreen}
          pgwRequest={pgwRequest}
        />
      </ModalLayout>
      {showDepositModal && (
        <WalletDepositModal
          hide={() => setShowDepositModal(false)}
          walletsType={walletCurrenciesTypes.CRYPTO}
          wallet={extraCollateralOptions?.collateralWallet}
          ledgerAccount={ledgerAccounts[extraCollateralOptions?.collateralWallet?.coin]}
        />
      )}

      {showReceiveCryptoModal && (
        <WalletDepositModal
          hide={() => setShowReceiveCryptoModal(false)}
          walletsType={walletCurrenciesTypes.CRYPTO}
          wallet={extraCollateralOptions?.collateralWallet}
          ledgerAccount={ledgerAccounts[extraCollateralOptions?.collateralWallet?.coin]}
          depositOptions={{
            wallet: extraCollateralOptions.collateralWallet,
            minDepositAmount: extraCollateralOptions.minCollateralAmountNeeded,
          }}
        />
      )}

      {showExchangeModal && (
        <ExchangeModal
          wallet={calculatorForm?.collateralWallet}
          hide={() => setShowExchangeModal(false)}
          fiatCurrencyCode={fiatCurrencyCode}
          supportedCurrencies={ledgerFilteredCurrencies}
        />
      )}
    </LoansLayout>
  );
};

export default Loans;
